import { useState, useContext } from 'react';
import { GenericItem } from '../../types/generic.types';
import { useTheme } from '@mui/material/styles';
import {
  DataGrid,
  GridColDef,
  GridRenderCellParams,
  GridToolbar,
  esES 
} from '@mui/x-data-grid';
import {
  Box,
  IconButton,
  Tooltip,
  Switch,
  useMediaQuery
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { DarkModeContext } from '../../../context/darkModeContext';
import "./genericDataTable.scss";
interface GenericDataTableProps {
  items: GenericItem[]; 
  title: string;
  onEdit: (item: GenericItem) => void;
  onDelete: (id: number) => void;
  onStatusChange: (id: number, active: boolean) => void;
  onAdd: () => void;
  isLoading: boolean;
}
const GenericDataTable = ({
  items,
  title,
  onEdit,
  onDelete,
  onStatusChange,
  onAdd,
  isLoading
}: GenericDataTableProps) => {
  const [pageSize, setPageSize] = useState<number>(10);
  const { darkMode } = useContext(DarkModeContext);
  // Tema dinámico con tu paleta
  const handleDelete = (id: number) => {
    Swal.fire({
      title: '¿Estás seguro?',
      text: "Esta acción no se puede revertir",
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#7451F8',
      cancelButtonColor: '#F44336',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
      background: darkMode ? '#2d2d2d' : '#ffffff',
      color: darkMode ? '#ffffff' : '#333333',
      iconColor: '#FF9800',
    }).then((result) => {
      if (result.isConfirmed) {
        onDelete(id);
      }
    });
  };
  const columns: GridColDef[] = [
    { 
      field: 'Id', 
      headerName: 'ID', 
      width: 90,
      type: 'number',
    },
    {
      field: 'Nombre',
      headerName: 'Nombre',
      flex: 1,
      minWidth: 200,
    },
    {
      field: 'Activo',
      headerName: 'Estado',
      width: 120,
      renderCell: (params: GridRenderCellParams<GenericItem>) => (
        <Switch
          checked={params.row.Activo}
          onChange={(e) => onStatusChange(params.row.Id, e.target.checked)}
          color="primary"
        />
      ),
    },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 120,
      sortable: false,
      filterable: false,
      renderCell: (params: GridRenderCellParams<GenericItem>) => (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title="Editar">
            <IconButton 
              onClick={() => onEdit(params.row)}
              color="primary"
              size="small"
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Eliminar">
            <IconButton
              onClick={() => handleDelete(params.row.Id)}
              color="error"
              size="small"
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];
  return (
    <div className={`generic-datatable ${darkMode ? 'dark-mode' : 'light-mode'}`}>
        <div className="datatable-header">         
          <button className="add-button" onClick={onAdd}>
            Agregar Nuevo
          </button>
        </div>
        <DataGrid
          rows={items}
          columns={columns}
          pageSize={pageSize}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          rowsPerPageOptions={[5, 10, 20]}
          pagination
          checkboxSelection={false}
          disableSelectionOnClick
          autoHeight
          loading={isLoading}
          getRowId={(row) => row.Id}
          components={{
            Toolbar: GridToolbar
          }}
          componentsProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          sx={{
            borderRadius: '12px',
            border: 'none',
            '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
              outline: 'none',
            },
          }}
        />
      </div>
  );
};
export default GenericDataTable;